import React, { useEffect, useMemo, useState } from "react";
import { ColorAddedPlayer, IPlayerComparisonData } from "../../types/IPlayerComparison";
import { ScattrPlot } from "./ScattrPlot";
import { generateRandomUniqueColors, IPlotFilter, PlotPlayerSiderbarList } from "./ScattrPlotUtils";
import { useLoading } from "../../contexts/LoadingContext";
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import CloseIcon from "@mui/icons-material/Close";
import { addUpdateSortListData } from "./addOrUpdateSortList";
import Collpase from "../../assets/icons/Collapse.png";
import { useNavigate } from "react-router-dom";
import SecondaryButton from "../../ui/SecondaryButton";
import LabelText from "../../ui/LabelText";
import Switch from "../../ui/CustomSwitch";
import RemoveIcon from "../../assets/icons/remove.png";
import { useNewPlayerShortlistContext } from "../../contexts/NewPlayerShortlistContext";
import { IPlayerCardDetail } from "../../types/IPlayerCardDetail";
import { fetchPlayerCardData } from "../../pages/Chemistry/Utils";

interface PlotComparisonProps {
  playerComparisonData: IPlayerComparisonData[];
  plotFilters: IPlotFilter;
  isFormSortList: boolean;
  acrossSortDirection: "inc" | "dec";
  upSortDirection: "desc" | "asc";
  benchMarkPlayerId: number;
}

export const PlotComparison: React.FC<PlotComparisonProps> = ({
  playerComparisonData,
  plotFilters,
  isFormSortList,
  acrossSortDirection,
  upSortDirection,
  benchMarkPlayerId,
}) => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const { showNotification, hideNotification, displayNotification, setDisplayNotification, notificationContent, setNotificationContent } =
    useNotification();
  const [isPlayerPosition, setIsPlayerPosition] = useState<boolean>(false);
  const [isPlayerLeague, setIsPlayerLeague] = useState<boolean>(false);
  const playerComparisonTableData = useMemo(
    () =>
      playerComparisonData?.map((player) => ({
        ...player,
        finishingShots:
          isPlayerLeague && isPlayerPosition
            ? player?.playerFinalThirdLeague?.finishingShotsByPosition
            : isPlayerLeague
            ? player?.playerFinalThirdLeague?.finishingShots
            : isPlayerPosition
            ? player?.playerFinalThirdUniverse?.finishingShotsByPosition
            : player?.playerFinalThirdUniverse?.finishingShots,
        finishingAerial:
          isPlayerLeague && isPlayerPosition
            ? player?.playerFinalThirdLeague?.finishingAerialByPosition
            : isPlayerLeague
            ? player?.playerFinalThirdLeague?.finishingAerial
            : isPlayerPosition
            ? player?.playerFinalThirdUniverse?.finishingAerialByPosition
            : player?.playerFinalThirdUniverse?.finishingAerial,
        attackPositioning:
          isPlayerLeague && isPlayerPosition
            ? player?.playerFinalThirdLeague?.attackPositioningByPosition
            : isPlayerLeague
            ? player?.playerFinalThirdLeague?.attackPositioning
            : isPlayerPosition
            ? player?.playerFinalThirdUniverse?.attackPositioningByPosition
            : player?.playerFinalThirdUniverse?.attackPositioning,
        creativity:
          isPlayerLeague && isPlayerPosition
            ? player?.playerBuildUpPlayLeague?.creativityByPosition
            : isPlayerLeague
            ? player?.playerBuildUpPlayLeague?.creativity
            : isPlayerPosition
            ? player?.playerBuildUpPlayUniverse?.creativityByPosition
            : player?.playerBuildUpPlayUniverse?.creativity,
        passingAccuracy:
          isPlayerLeague && isPlayerPosition
            ? player?.playerBuildUpPlayLeague?.passingAccuracyByPosition
            : isPlayerLeague
            ? player?.playerBuildUpPlayLeague?.passingAccuracy
            : isPlayerPosition
            ? player?.playerBuildUpPlayUniverse?.passingAccuracyByPosition
            : player?.playerBuildUpPlayUniverse?.passingAccuracy,
        dribbling:
          isPlayerLeague && isPlayerPosition
            ? player?.playerBuildUpPlayLeague?.dribblingByPosition
            : isPlayerLeague
            ? player?.playerBuildUpPlayLeague?.dribbling
            : isPlayerPosition
            ? player?.playerBuildUpPlayUniverse?.dribblingByPosition
            : player?.playerBuildUpPlayUniverse?.dribbling,
        crossing:
          isPlayerLeague && isPlayerPosition
            ? player?.playerBuildUpPlayLeague?.crossingByPosition
            : isPlayerLeague
            ? player?.playerBuildUpPlayLeague?.crossing
            : isPlayerPosition
            ? player?.playerBuildUpPlayUniverse?.crossingByPosition
            : player?.playerBuildUpPlayUniverse?.crossing,
        tackling:
          isPlayerLeague && isPlayerPosition
            ? player?.playerDefenceLeague?.tacklingByPosition
            : isPlayerLeague
            ? player?.playerDefenceLeague?.tackling
            : isPlayerPosition
            ? player?.playerDefenceUniverse?.tacklingByPosition
            : player?.playerDefenceUniverse?.tackling,
        defensiveHeaders:
          isPlayerLeague && isPlayerPosition
            ? player?.playerDefenceLeague?.defensiveHeadersByPosition
            : isPlayerLeague
            ? player?.playerDefenceLeague?.defensiveHeaders
            : isPlayerPosition
            ? player?.playerDefenceUniverse?.defensiveHeadersByPosition
            : player?.playerDefenceUniverse?.defensiveHeaders,
        aggression:
          isPlayerLeague && isPlayerPosition
            ? player?.playerDefenceLeague?.aggressionByPosition
            : isPlayerLeague
            ? player?.playerDefenceLeague?.aggression
            : isPlayerPosition
            ? player?.playerDefenceUniverse?.aggressionByPosition
            : player?.playerDefenceUniverse?.aggression,
      })),
    [playerComparisonData, isPlayerLeague, isPlayerPosition]
  );
  const colorsRange = useMemo(() => generateRandomUniqueColors(playerComparisonTableData?.length), [playerComparisonData]);
  const [hoveredPlayer, setHoveredPlayer] = useState<number>(0);
  const { loading, startLoading, stopLoading } = useLoading();
  const [selectedPlayers, setSelectedPlayers] = useState<number[]>([]);
  const [hiddenPlayers, setHiddenPlayers] = useState<number[]>([]);
  const [isChunkActive, setIsChunkActive] = useState<boolean>(false);
  const [selectedSimilarPlayer, setSelectedSimilarPlayer] = useState<ColorAddedPlayer | null>(null);
  const [scoreByComparison, setIsScoreByComparison] = useState<boolean>(false);
  const [colorsAddedPlayers, setColorsAddedPlayers] = useState(
    playerComparisonTableData?.map((player, i) => ({ ...player, colorCode: colorsRange[i] })) as ColorAddedPlayer[]
  );
  useEffect(
    () =>
      setColorsAddedPlayers(
        playerComparisonTableData?.map((player, i) => ({ ...player, colorCode: colorsRange[i] })) as ColorAddedPlayer[]
      ),
    [playerComparisonTableData]
  );
  const filteredPlayers = useMemo(
    () => colorsAddedPlayers?.filter((player) => !hiddenPlayers?.includes(player?.playerId)),
    [hiddenPlayers, colorsAddedPlayers, playerComparisonTableData]
  );
  const handleHiddenPlayers = () => {
    setHiddenPlayers((prev) => [...prev, ...selectedPlayers]);
    setSelectedPlayers([]);
  };
  const handleAllSelected = () => {
    setSelectedPlayers(playerComparisonTableData?.map((player) => player?.playerId));
    setHiddenPlayers([]);
  };
  const handleRemovePlayer = (playersToRemove: number[], playerToRemoveFrom: "hidden" | "selected") => {
    addUpdateSortListData(
      userData,
      playersToRemove,
      startLoading,
      stopLoading,
      setDisplayNotification,
      setNotificationContent,
      true,
      navigate
    );
    if (playerToRemoveFrom === "hidden") {
      setHiddenPlayers([]);
    }
    if (playerToRemoveFrom === "selected") {
      setSelectedPlayers([]);
    }
    setColorsAddedPlayers((prevData) => prevData.filter((player) => !playersToRemove.includes(player.playerId)));
  };

  const handleRemoveSinglePlayer = (playersToRemove: number) => {
    addUpdateSortListData(userData, [playersToRemove], startLoading, stopLoading, setDisplayNotification, setNotificationContent, true);
    if (hiddenPlayers?.includes(playersToRemove)) {
      setHiddenPlayers((prev) => prev.filter((player) => player !== playersToRemove));
    }
    if (selectedPlayers?.includes(playersToRemove)) {
      setSelectedPlayers((prev) => prev.filter((player) => player !== playersToRemove));
    }
    setColorsAddedPlayers((prevData) => prevData.filter((player) => player?.playerId !== playersToRemove));
  };

  useEffect(() => {
    if (benchMarkPlayerId) {
      const benchmark = colorsAddedPlayers.find((player) => player.playerId === benchMarkPlayerId) || null;
      setSelectedSimilarPlayer(benchmark);
    } else {
      setSelectedSimilarPlayer(null);
    }
  }, [playerComparisonTableData, benchMarkPlayerId]);

  const handlePositionToggle = () => {
    startLoading();
    setIsPlayerPosition((prevState) => !prevState);
    setTimeout(() => stopLoading(), 500);
  };
  const handleLeagueToggle = () => {
    startLoading();
    setIsPlayerLeague((prevState) => !prevState);
    setTimeout(() => stopLoading(), 500);
  };

  const { newlyAddedPlayerCount, setNewlyAddedPlayerCount } = useNewPlayerShortlistContext();
  const [playerCardDetails, setPlayerCardDeatils] = useState<IPlayerCardDetail | undefined>(undefined);
  const [playerCardID, setPlayerCardID] = useState<number>(0);
  useEffect(() => {
    if (playerCardID > 0) {
      setPlayerCardDeatils(undefined);
      fetchPlayerCardData(startLoading, stopLoading, playerCardID, setPlayerCardDeatils);
    } else {
      setPlayerCardDeatils(undefined);
    }
  }, [playerCardID]);

  return (
    <div className="plot-comparison-container">
      <PlotPlayerSiderbarList
        isNickName={false}
        isChunkActive={isChunkActive}
        handleRemoveSinglePlayer={handleRemoveSinglePlayer}
        isFormSortList={isFormSortList}
        setHiddenPlayers={setHiddenPlayers}
        hiddenPlayers={hiddenPlayers}
        selectedSimilarPlayer={selectedSimilarPlayer}
        setSelectedPlayers={setSelectedPlayers}
        selectedPlayers={selectedPlayers}
        playerComparisonTableData={colorsAddedPlayers}
        hoveredPlayer={hoveredPlayer}
        setHoveredPlayer={setHoveredPlayer}
        playerCardDetails={playerCardDetails}
        playerCardID={playerCardID}
        setPlayerCardID={setPlayerCardID}
        setPlayerCardDeatils={setPlayerCardDeatils}
      />
      <div className="scattr-plot-container">
        <ScattrPlot
          benchMarkPlayerId={benchMarkPlayerId}
          setSelectedPlayers={setSelectedPlayers}
          upSortDirection={upSortDirection}
          acrossSortDirection={acrossSortDirection}
          selectedPlayers={selectedPlayers}
          plotFilters={plotFilters}
          setHoveredPlayer={setHoveredPlayer}
          playerComparisonTableData={playerComparisonTableData}
          filteredPlayers={filteredPlayers}
          hoveredPlayer={hoveredPlayer}
          playerCardID={playerCardID}
          setPlayerCardID={setPlayerCardID}
        />
      </div>
      <div className="plot-comparison-toolbar">
        <div>
          <SecondaryButton iconType="score" onClick={() => setIsScoreByComparison(!scoreByComparison)} isSelected={scoreByComparison} />
          {selectedPlayers?.length !== playerComparisonTableData?.length && <button onClick={() => handleAllSelected()}>Select all</button>}
          {selectedPlayers?.length > 0 && <button onClick={() => setSelectedPlayers([])}>Deselect all</button>}
          {hiddenPlayers?.length > 0 && (
            <button className={`chunk-icon ${isChunkActive && "chunk-active-icon"}`} onClick={() => setIsChunkActive(!isChunkActive)}>
              <img src={Collpase} alt="" />
            </button>
          )}
          {hiddenPlayers?.length > 0 && isFormSortList && (
            <button onClick={() => handleRemovePlayer(hiddenPlayers, "hidden")} className="remove-hidden-players-button">
              <img src={RemoveIcon} height={24} width={24} />
              Remove hidden players
            </button>
          )}
        </div>
        {selectedPlayers?.length > 0 && (
          <div>
            <p>SELECTION OPTIONS:</p>
            <button onClick={() => handleHiddenPlayers()}>Hide selected</button>
            {selectedPlayers?.length !== playerComparisonTableData?.length && (
              <button
                onClick={() =>
                  setHiddenPlayers(
                    playerComparisonTableData
                      ?.filter((player) => !selectedPlayers?.includes(player?.playerId))
                      ?.map((player) => player?.playerId)
                  )
                }
              >
                Hide not selected
              </button>
            )}
            {!isFormSortList && (
              <button
                onClick={() => {
                  setNewlyAddedPlayerCount((prev) => prev + selectedPlayers?.length);
                  addUpdateSortListData(
                    userData,
                    selectedPlayers,
                    startLoading,
                    stopLoading,
                    setDisplayNotification,
                    setNotificationContent,
                    false,
                    navigate
                  );
                }}
              >
                Add to shortlist
              </button>
            )}
            {isFormSortList && (
              <button onClick={() => handleRemovePlayer(selectedPlayers, "selected")} className="remove-hidden-players-button">
                Remove from shortlist
              </button>
            )}
          </div>
        )}
      </div>
      {scoreByComparison && (
        <div className="comparison-by-score-container">
          <div className="comparison-by-score-content">
            <LabelText className="comparison-score-text">Attributes scored in relation to...</LabelText>
            <Switch
              id="position"
              toggled={isPlayerPosition}
              onChange={handlePositionToggle}
              source="Filter"
              title="Player’s position"
              isByComparison={true}
            />
            <Switch
              id="league"
              toggled={isPlayerLeague}
              onChange={handleLeagueToggle}
              source="Filter"
              title="Player’s league"
              isByComparison={true}
            />
          </div>
          <div className="score-notification-closeIcon">
            <CloseIcon sx={{ color: "white" }} onClick={() => setIsScoreByComparison(!scoreByComparison)} />
          </div>
        </div>
      )}
    </div>
  );
};
